import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useMe } from '@/api/domains/merchant-settings';
import { useUpdateOrderSelectedPoint } from '@/api/domains/order-shipments';
import type { Order } from '@/api/types/orders';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toApiProblem, toastProblem } from '@/lib/errors';

const GEOWIDGET_TAG = 'inpost-geowidget';
// The geowidget dispatches a DOM event whose name is the value of its `onpoint`
// attribute; we listen for it on `document` (the event bubbles up there).
const POINT_EVENT = 'onpointselect';
// Mirror the backend's point_id rule (UpdateOrderSelectedPointRequest) as a
// defense-in-depth guard against a misbehaving widget payload.
const POINT_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

type SdkState = 'loading' | 'ready' | 'failed';

/**
 * Lazily injects the InPost Geowidget SDK (script + stylesheet) the first time
 * the picker opens, then resolves once the custom element is defined. Idempotent
 * across mounts - the asset tags are tagged and reused.
 */
function useGeowidgetSdk(scriptUrl: string, styleUrl: string, active: boolean): SdkState {
  const [state, setState] = useState<SdkState>(() =>
    typeof customElements !== 'undefined' && customElements.get(GEOWIDGET_TAG)
      ? 'ready'
      : 'loading',
  );

  useEffect(() => {
    if (!active) return;
    if (typeof document === 'undefined' || typeof customElements === 'undefined') return;

    if (customElements.get(GEOWIDGET_TAG)) {
      setState('ready');
      return;
    }

    // Re-arm loading on every (re)open so a prior load failure can be retried.
    setState('loading');

    if (styleUrl && !document.querySelector('link[data-inpost-geowidget]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = styleUrl;
      link.dataset.inpostGeowidget = 'true';
      document.head.appendChild(link);
    }

    let cancelled = false;

    let script = document.querySelector<HTMLScriptElement>('script[data-inpost-geowidget]');
    if (!script) {
      script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.dataset.inpostGeowidget = 'true';
      script.addEventListener('error', () => {
        // Drop the stale tag so the next open re-injects and can recover.
        script?.remove();
        if (!cancelled) setState('failed');
      });
      document.head.appendChild(script);
    }

    void customElements.whenDefined(GEOWIDGET_TAG).then(() => {
      if (!cancelled) setState('ready');
    });

    return () => {
      cancelled = true;
    };
  }, [active, scriptUrl, styleUrl]);

  return state;
}

interface PointMapDialogProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * When provided, the picked point is reported to the caller instead of being
   * saved to the order - used for split shipments, which must not change the
   * order's selected point. Without it, the dialog PATCHes the order directly.
   */
  onSelect?: (pointId: string) => void;
}

const noticeClasses = 'rounded-[10px] bg-[#fff8d6] p-4 text-sm text-[#1a1a1a]';
const ghostButtonClasses =
  'flex h-9 items-center justify-center rounded-[10px] border border-black bg-transparent px-4 text-sm font-medium leading-5 text-black hover:bg-accent';

/**
 * Admin point picker: embeds the InPost Geowidget (same SDK as the buyer page)
 * in a modal. Picking a point applies it immediately (no separate confirm) -
 * either to the order via `PATCH /admin/orders/{id}/selected-point`, or, when an
 * `onSelect` callback is given, by reporting it to the caller (split shipments).
 */
export function PointMapDialog({ order, open, onOpenChange, onSelect }: PointMapDialogProps) {
  const { t, i18n } = useTranslation('orders');
  const meQuery = useMe();
  const updatePoint = useUpdateOrderSelectedPoint(order.id);

  const token = meQuery.data?.settings.inpost_geowidget_token ?? '';
  const geowidget = meQuery.data?.geowidget ?? null;
  const language = i18n.language.toLowerCase().startsWith('pl') ? 'pl' : 'en';
  const countryCode = order.shopify_shipping_address?.country_code ?? null;

  // Pending rAF handle for the deferred geowidget creation (see mountGeowidget).
  const rafRef = useRef<number | null>(null);
  // Holds the latest apply logic so the point-select listener (subscribed once
  // per open) always calls fresh props/state without re-subscribing.
  const applyRef = useRef<(pointId: string) => void>(() => {});

  const sdkState = useGeowidgetSdk(
    geowidget?.script_url ?? '',
    geowidget?.style_url ?? '',
    open && Boolean(token) && geowidget !== null,
  );

  // Picking a point in the widget already requires clicking "select", so there's
  // no separate confirm step: apply the point immediately and close the modal.
  useEffect(() => {
    applyRef.current = (pointId: string) => {
      if (!POINT_ID_PATTERN.test(pointId)) return;

      // Split-shipment mode: hand the point back to the caller, never touch the order.
      if (onSelect) {
        onSelect(pointId);
        onOpenChange(false);
        return;
      }

      if (updatePoint.isPending) return;
      updatePoint.mutate(
        { point_id: pointId },
        {
          onSuccess: () => {
            toast.success(t('detail.pointUpdated'));
            onOpenChange(false);
          },
          onError: (error) => toastProblem(toApiProblem(error)),
        },
      );
    };
  });

  useEffect(() => {
    if (!open) return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ name?: string; id?: string } | null>).detail;
      const id = detail?.name ?? detail?.id ?? null;
      if (id) applyRef.current(String(id));
    };
    document.addEventListener(POINT_EVENT, handler);
    return () => document.removeEventListener(POINT_EVENT, handler);
  }, [open]);

  // Build and mount the geowidget via a callback ref rather than an effect: the
  // container node is freshly mounted on every open (it only renders once the SDK
  // is ready), so the ref reliably re-fires each time - an effect keyed on `open`
  // failed to re-create the widget on the 2nd open. Creation is deferred one
  // frame so the dialog's open animation/layout has settled; building it
  // synchronously (as on the 2nd open, when the SDK is already loaded) renders a
  // blank map. The element is created imperatively with attributes set before it
  // connects because React 19 would assign `token` as a getter-only DOM property.
  const mountGeowidget = useCallback(
    (container: HTMLDivElement | null) => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      if (container === null || !token || geowidget === null) {
        return;
      }

      rafRef.current = requestAnimationFrame(() => {
        const widget = document.createElement(GEOWIDGET_TAG);
        widget.setAttribute('token', token);
        widget.setAttribute('language', language);
        widget.setAttribute('config', geowidget.config);
        widget.setAttribute('onpoint', POINT_EVENT);
        if (countryCode) {
          widget.setAttribute('country', countryCode);
        }
        widget.style.display = 'block';
        widget.style.height = '60vh';
        widget.style.minHeight = '420px';
        widget.style.width = '100%';
        widget.style.borderRadius = '10px';
        widget.style.overflow = 'hidden';
        container.replaceChildren(widget);
      });
    },
    [token, geowidget, language, countryCode],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[900px]">
        <DialogHeader>
          <DialogTitle>{t('detail.mapDialog.title')}</DialogTitle>
          <DialogDescription>{t('detail.mapDialog.description')}</DialogDescription>
        </DialogHeader>

        {meQuery.isLoading ? (
          <div className="grid h-[60vh] min-h-[420px] place-items-center text-sm text-muted-foreground">
            {t('detail.mapDialog.loading')}
          </div>
        ) : !token || !geowidget ? (
          <p className={noticeClasses}>{t('detail.mapDialog.tokenMissing')}</p>
        ) : sdkState === 'failed' ? (
          <p className={noticeClasses}>{t('detail.mapDialog.loadFailed')}</p>
        ) : sdkState === 'loading' ? (
          <div className="grid h-[60vh] min-h-[420px] place-items-center text-sm text-muted-foreground">
            {t('detail.mapDialog.loading')}
          </div>
        ) : (
          <div ref={mountGeowidget} className="w-full" />
        )}

        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} className={ghostButtonClasses}>
            {t('detail.mapDialog.cancel')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
